import { createEffect, For, onCleanup, Show } from "solid-js"
import { useDialog } from "@opencode-ai/ui/context/dialog"
import { ButtonV2 } from "@opencode-ai/ui/v2/button-v2"
import { Dialog, DialogBody, DialogFooter, DialogHeader, DialogTitle } from "@opencode-ai/ui/v2/dialog-v2"
import { DividerV2 } from "@opencode-ai/ui/v2/divider-v2"
import { useLanguage } from "@/context/language"

type ConfirmationOptions = {
  title: string; confirmLabel: string; message: string; items?: readonly string[]
  valid?: () => boolean; variant?: "contrast" | "danger"
}

export function useConfirmation() {
  const dialog = useDialog()
  const language = useLanguage()
  let alive = true
  let cancelPending: (() => void) | undefined
  onCleanup(() => { alive = false; cancelPending?.() })

  return (options: ConfirmationOptions): Promise<boolean> => {
    if (!alive || cancelPending || options.valid?.() === false) return Promise.resolve(false)
    const confirmLabel = options.confirmLabel?.trim() || language.t("common.confirm")
    const title = options.title?.trim() || confirmLabel
    return new Promise((resolve) => {
      let settled = false, accepted = false
      const finish = (value: boolean) => {
        if (settled) return
        settled = true
        cancelPending = undefined
        resolve(value)
      }
      const cancel = () => {
        if (settled) return
        accepted = false; finish(false); dialog.close()
      }
      cancelPending = cancel
      void Promise.resolve(dialog.show(() => {
        // Wait for the modal to close before the request, so focus restoration cannot hide an inline error.
        onCleanup(() => finish(accepted && alive && options.valid?.() !== false))
        createEffect(() => { if (!alive || options.valid?.() === false) queueMicrotask(cancel) })
        return <Dialog fit>
          <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
          <DividerV2 />
          <DialogBody class="flex w-96 max-w-full flex-col gap-3 px-4 pt-4 pb-1">
            <p class="break-words text-14-regular text-v2-text-text-base">{options.message}</p>
            <Show when={options.items?.length}>
              <ul class="max-h-60 overflow-y-auto text-12-regular text-v2-text-text-muted">
                <For each={options.items}>{(name) => <li class="py-1 break-words">{name}</li>}</For>
              </ul>
            </Show>
          </DialogBody>
          <DialogFooter>
            <ButtonV2 type="button" variant="neutral" autofocus onClick={cancel}>{language.t("common.cancel")}</ButtonV2>
            <ButtonV2 type="button" variant={options.variant ?? "contrast"} onClick={() => {
              if (settled || !alive || options.valid?.() === false) return
              accepted = true
              dialog.close()
            }}>{confirmLabel}</ButtonV2>
          </DialogFooter>
        </Dialog>
      }, () => { if (!accepted) finish(false) })).catch(() => finish(false))
    })
  }
}

export function useDeleteConfirmation() {
  const confirm = useConfirmation()
  const language = useLanguage()
  return (options: Omit<ConfirmationOptions, "title" | "confirmLabel" | "variant">) => confirm({
    ...options, title: language.t("common.delete"), confirmLabel: language.t("common.delete"), variant: "danger",
  })
}

// Native dialogs paused the page. Keep that reviewed state stable while an app modal is open.
export function useScopedConfirmation(snapshot: () => unknown) {
  const confirm = useConfirmation()
  const language = useLanguage()
  return async (message: string, label = language.t("common.confirm"), variant: "contrast" | "danger" = "contrast") => {
    const reviewed = JSON.stringify(snapshot())
    const valid = () => JSON.stringify(snapshot()) === reviewed
    return await confirm({ title: label, confirmLabel: label, message, variant, valid }) && valid()
  }
}
